import * as React from "react";
import * as ReactDOM from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Gamepad2,
  Search,
  Menu,
  X,
  Home,
  Users,
  Info,
  Headphones,
  FileText,
  ExternalLink,
  Sun,
  Moon,
  Swords,
  Target,
  Trophy,
  Zap,
  Flame,
  Shield,
  Crosshair,
  Joystick,
  Monitor,
  Loader2,
  Download,
  MessageCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";

// Icon mapping for navigation
const iconMap = {
  "home": Home,
  "mağaza": Home,
  "store": Home,
  "programlar": Monitor,
  "programs": Monitor,
  "topluluk": Users,
  "community": Users,
  "hakkında": Info,
  "about": Info,
  "hakkinda": Info,
  "destek": Headphones,
  "support": Headphones,
  "siteler": ExternalLink,
  "websites": ExternalLink,
  "linkler": ExternalLink,
  "kaynaklar": FileText,
};

// Floating game icons for background animation
const floatingIcons = [
  { Icon: Gamepad2, delay: 0, duration: 15, x: 10, y: 20 },
  { Icon: Swords, delay: 2, duration: 18, x: 25, y: 60 },
  { Icon: Target, delay: 4, duration: 12, x: 40, y: 30 },
  { Icon: Trophy, delay: 1, duration: 20, x: 55, y: 70 },
  { Icon: Zap, delay: 3, duration: 14, x: 70, y: 25 },
  { Icon: Flame, delay: 5, duration: 16, x: 85, y: 55 },
  { Icon: Shield, delay: 2.5, duration: 17, x: 15, y: 80 },
  { Icon: Crosshair, delay: 4.5, duration: 13, x: 60, y: 85 },
  { Icon: Joystick, delay: 1.5, duration: 19, x: 90, y: 40 },
  { Icon: Gamepad2, delay: 3.5, duration: 15, x: 35, y: 15 },
];

// Animated background component - DISABLED for performance
function AnimatedBackground() {
  // Disabled for better performance on low-end devices
  return null;
}

// Animated text rotator for logo - Typewriter effect
const rotatingWords = ["Oyun", "Games", "İndir"];

function AnimatedLogoText({ textColor }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [displayText, setDisplayText] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showCursor, setShowCursor] = React.useState(true);

  React.useEffect(() => {
    const currentWord = rotatingWords[currentIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          // Word complete, wait then start deleting
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          // Deletion complete, move to next word
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % rotatingWords.length);
        }
      }
    }, isDeleting ? 80 : 120); // Faster delete, slower type

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentIndex]);

  // Cursor blink effect
  React.useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  const baseStyle = textColor 
    ? { color: textColor }
    : { 
        background: 'linear-gradient(90deg, rgb(167 139 250), rgb(244 114 182), rgb(56 189 248), rgb(167 139 250))',
        backgroundSize: '200% auto',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        animation: 'shimmer 3s linear infinite',
      };

  return (
    <div className="relative font-extrabold text-xl sm:text-2xl tracking-tight origin-left flex min-w-[140px] sm:min-w-[160px]">
      <span 
        className="relative animate-text-shimmer bg-[length:200%_auto]"
        style={baseStyle}
      >
        Crack
      </span>
      <span 
        className="relative animate-text-shimmer bg-[length:200%_auto]"
        style={baseStyle}
      >
        {displayText}
      </span>
      <span 
        className={cn(
          "relative transition-opacity duration-100",
          showCursor ? "opacity-100" : "opacity-0"
        )}
        style={baseStyle}
      >
        _
      </span>
    </div>
  );
}

// Modern Logo Component - Performance Optimized
function ModernLogo({ siteTitle, logoUrl, logoBackgroundColor, siteTitleColor, logoBackgroundTransparent }) {
  const logoSrc = logoUrl?.startsWith('/') 
    ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` 
    : logoUrl;

  const bgColor = logoBackgroundTransparent ? 'transparent' : (logoBackgroundColor || null);
  const textColor = siteTitleColor || null;
  const isTransparent = logoBackgroundTransparent === true;

  return (
    <div className="flex items-center gap-3 sm:gap-4 shrink-0 group">
      {/* Logo Icon - Bigger with animation */}
      <div className="relative">
        {/* Glow effect behind logo */}
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-50 blur-lg transition-all duration-500 animate-pulse-slow" />
        
        <div className={cn(
          "relative size-14 sm:size-16 rounded-xl sm:rounded-2xl overflow-hidden transition-transform duration-300 group-hover:scale-105",
          isTransparent ? "bg-transparent" : "bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a]"
        )}>
          {/* Icon background */}
          <div 
            className={cn(
              "relative size-full rounded-[10px] sm:rounded-xl flex items-center justify-center overflow-hidden",
              isTransparent && "bg-transparent"
            )}
            style={{ 
              background: isTransparent 
                ? 'transparent' 
                : (bgColor || 'linear-gradient(to bottom right, rgb(124 58 237), rgb(147 51 234), rgb(219 39 119))')
            }}
          >
            {logoSrc ? (
              <img 
                src={logoSrc} 
                alt={siteTitle}
                className="relative size-10 sm:size-12 object-contain animate-float"
                loading="lazy"
                onError={(e) => { 
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <Gamepad2 
              className={cn(
                "relative size-7 sm:size-8 text-white animate-float",
                logoSrc && "hidden"
              )} 
            />
          </div>
        </div>
      </div>

      {/* Logo Text - With rotating animation */}
      <div className="leading-tight">
        <AnimatedLogoText textColor={textColor} />
        <div className="text-[10px] sm:text-xs text-muted-foreground/70 hidden xs:flex items-center gap-1">
          <span className="inline-block size-1.5 rounded-full bg-green-500 animate-pulse" />
          oyun indirme platformu
        </div>
      </div>
      
      {/* Inline keyframes for shimmer */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export const SiteTopBar = ({ onOpenRequestForm, onSearch }) => {
  const [query, setQuery] = React.useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isDarkMode, setIsDarkMode] = React.useState(true);
  const [searchResults, setSearchResults] = React.useState([]);
  const [showResults, setShowResults] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [messageModalOpen, setMessageModalOpen] = React.useState(false);
  const searchRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const searchTimeoutRef = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, logo, favicon, logoBackgroundTransparent: storeLogoTransparent, logoBackgroundColor: storeLogoBgColor, siteTitleColor: storeTitleColor } = useStore();

  const siteTitle = settings?.siteTitle || "CrackOyun";
  const navLinks = Array.isArray(settings?.navLinks) ? settings.navLinks : null;
  const websitesTabName = settings?.websitesTabName || "Yararlı Siteler";
  const gamesTabName = settings?.gamesTabName || "OYUNLAR";
  const programsTabName = settings?.programsTabName || "PROGRAMLAR";
  const customTab1Name = settings?.customTab1Name || "TOPLULUK";
  const customTab1Href = settings?.customTab1Href || "/community";
  const gamesTabColor = settings?.gamesTabColor || "#ffffff";
  const programsTabColor = settings?.programsTabColor || "#ffffff";
  const websitesTabColor = settings?.websitesTabColor || "#ffffff";
  const logoUrl = logo || settings?.logo || "";
  const logoBackgroundColor = storeLogoBgColor || settings?.logoBackgroundColor || "";
  const logoBackgroundTransparent = storeLogoTransparent || settings?.logoBackgroundTransparent || false;
  const siteTitleColor = storeTitleColor || settings?.siteTitleColor || "";
  
  // Message button settings
  const messageButtonEnabled = settings?.messageButtonEnabled || false;
  const messageButtonLabel = settings?.messageButtonLabel || "Duyuru";
  const messageButtonTitle = settings?.messageButtonTitle || "Duyuru";
  const messageButtonContent = settings?.messageButtonContent || "";

  // Live search function
  const performLiveSearch = React.useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/games?search=${encodeURIComponent(searchQuery)}&limit=6`
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.games || []);
        setShowResults(true);
      }
    } catch (e) {
      console.error("Search error:", e);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle input change with debounce
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performLiveSearch(value);
    }, 300);
  };

  // Close results when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both searchRef AND dropdownRef
      const isOutsideSearch = searchRef.current && !searchRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      // Only close if click is outside both elements
      if (isOutsideSearch && (isOutsideDropdown || !dropdownRef.current)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generate slug for navigation
  const generateSlug = (title) => {
    if (!title) return "";
    const turkishMap = {
      'ı': 'i', 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c',
      'İ': 'i', 'Ğ': 'g', 'Ü': 'u', 'Ş': 's', 'Ö': 'o', 'Ç': 'c',
    };
    return title
      .toLowerCase()
      .split('')
      .map(c => turkishMap[c] || c)
      .join('')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim() + '-indir';
  };

  const resolvedTopNav = React.useMemo(() => {
    // Build 3 fixed tabs with customizable names and colors
    const fixedTabs = [
      { 
        label: gamesTabName.toUpperCase(), 
        href: "/", 
        icon: Home, 
        fixed: true,
        color: gamesTabColor
      },
      { 
        label: programsTabName.toUpperCase(), 
        href: "/programlar", 
        icon: Monitor, 
        fixed: true,
        color: programsTabColor
      },
      { 
        label: websitesTabName.toUpperCase(), 
        href: "/siteler", 
        icon: ExternalLink, 
        fixed: true,
        color: websitesTabColor
      },
    ];
    
    // Get labels of fixed tabs for filtering
    const fixedLabels = fixedTabs.map(t => t.label.toUpperCase());
    const fixedHrefs = ["/", "/programlar", "/siteler"];
    
    // If navLinks configured, add ONLY ACTIVE ones that don't duplicate fixed tabs
    if (navLinks !== undefined && navLinks !== null && navLinks.length > 0) {
      const userNav = navLinks
        .filter((x) => {
          // Must have label and href
          if (!x?.label || !x?.href) return false;
          // Must be active
          if (x.active === false) return false;
          // Must not duplicate fixed tab labels or hrefs
          const labelUpper = String(x.label).toUpperCase();
          const href = String(x.href);
          return !fixedLabels.includes(labelUpper) && !fixedHrefs.includes(href);
        })
        .map((x) => {
          const labelLower = String(x.label).toLowerCase();
          const Icon = iconMap[labelLower] || ExternalLink;
          return { 
            label: String(x.label).toUpperCase(), 
            href: String(x.href), 
            icon: Icon,
            fixed: false,
            color: "#ffffff"
          };
        });
      
      return [...fixedTabs, ...userNav];
    }
    
    // No custom links - return fixed tabs only
    return fixedTabs;
  }, [gamesTabName, programsTabName, websitesTabName, gamesTabColor, programsTabColor, websitesTabColor, navLinks]);

  // Theme toggle function
  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
      localStorage.setItem("theme", "light");
    }
  };

  // Load saved theme on mount
  React.useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    } else {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    }
  }, []);

  const isExternal = (href) => /^https?:\/\//i.test(String(href || ""));
  const isActive = (href) => !isExternal(href) && location.pathname === href;

  // Handle search form submit - only when user presses Enter or clicks search button
  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setShowResults(false);
      onSearch?.(query.trim());
    }
  };

  // Debounced search - filters main page while typing
  React.useEffect(() => {
    const t = setTimeout(() => {
      onSearch?.(query.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  return (
    <div className="relative rounded-2xl border border-white/10 glass-effect">
      {/* Animated Background Icons */}
      <AnimatedBackground />
      
      {/* Main Row */}
      <div className="relative flex items-center justify-between gap-2 p-3 sm:gap-4 sm:p-4">
        {/* Brand */}
        <Link to="/">
          <ModernLogo 
            siteTitle={siteTitle} 
            logoUrl={logoUrl} 
            logoBackgroundColor={logoBackgroundColor}
            siteTitleColor={siteTitleColor}
            logoBackgroundTransparent={logoBackgroundTransparent}
          />
        </Link>

        {/* Top nav - Desktop */}
        <div className="hidden md:block">
          <NavigationMenu>
            <NavigationMenuList className="gap-1">
              {resolvedTopNav.map((n, idx) => (
                <NavigationMenuItem key={`${n.href}-${idx}`}>
                  <NavigationMenuLink asChild>
                    {isExternal(n.href) ? (
                      <a
                        href={n.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: undefined }}
                        className={cn(
                          "h-10 rounded-xl px-4 py-2 text-sm font-medium",
                          "text-foreground",
                          "hover:bg-accent/50"
                        )}
                      >
                        {n.label}
                      </a>
                    ) : (
                      <Link
                        to={n.href}
                        className={cn(
                        "h-10 rounded-xl px-4 py-2 text-sm font-medium text-foreground",
                          isActive(n.href)
                            ? "bg-accent"
                            : "hover:bg-accent/50"
                        )}
                      >
                        {n.label}
                      </Link>
                    )}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Right side - Desktop */}
        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <div ref={searchRef} className="relative w-[200px] lg:w-[280px]">
            <form onSubmit={handleSearch}>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                value={query}
                onChange={handleQueryChange}
                onKeyDown={handleKeyDown}
                onFocus={() => query.length >= 2 && setShowResults(true)}
                placeholder="Oyun ara..."
                className={cn(
                  "h-10 rounded-full border-border/60 bg-background/25 pl-10 pr-10",
                  "shadow-none"
                )}
              />
              {isSearching ? (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground animate-spin" />
              ) : (
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full hover:bg-primary/20"
                  aria-label="Ara"
                >
                  <Search className="size-4 text-primary" />
                </Button>
              )}
            </form>
            {/* Live Search Results Dropdown - Rendered as Portal */}
            {showResults && searchResults.length > 0 && searchRef.current && ReactDOM.createPortal(
              <div 
                ref={dropdownRef}
                className="fixed bg-card border border-border rounded-2xl shadow-2xl"
                style={{
                  top: searchRef.current.getBoundingClientRect().bottom + 8,
                  left: searchRef.current.getBoundingClientRect().left,
                  width: 320,
                  zIndex: 99999,
                }}
                data-testid="search-dropdown"
              >
                <div className="p-2 max-h-[350px] overflow-y-auto">
                  <div className="text-xs text-muted-foreground px-3 py-2 border-b border-border/30 mb-1">
                    {searchResults.length} sonuç bulundu
                  </div>
                  {searchResults.map((game) => {
                    const gameSlug = generateSlug(game.title);
                    const gameUrl = `/${gameSlug}`;
                    return (
                      <div
                        key={game.id}
                        data-testid={`search-result-${game.id}`}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent transition-colors cursor-pointer no-underline text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowResults(false);
                          navigate(gameUrl);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowResults(false);
                            navigate(gameUrl);
                          }
                        }}
                        role="link"
                        tabIndex={0}
                      >
                        {/* Game Cover - pointer-events-none to ensure clicks bubble to parent */}
                        <div className="size-12 rounded-lg overflow-hidden bg-muted/30 shrink-0 pointer-events-none">
                          {(game.cover || game.images?.[0]) ? (
                            <img 
                              src={game.cover || game.images?.[0]} 
                              alt={game.title}
                              className="size-full object-cover"
                            />
                          ) : (
                            <div className="size-full flex items-center justify-center">
                              <Gamepad2 className="size-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        
                        {/* Game Info - pointer-events-none to ensure clicks bubble to parent */}
                        <div className="flex-1 min-w-0 pointer-events-none">
                          <div className="font-medium text-sm truncate">
                            {game.title}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            {game.category && (
                              <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px]">
                                {game.category}
                              </span>
                            )}
                            {game.downloads > 0 && (
                              <span className="flex items-center gap-1">
                                <Download className="size-3" />
                                {game.downloads.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* View All Results */}
                <div className="border-t border-border/40 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResults(false);
                      onSearch?.(query);
                      // Scroll to games section after a short delay
                      setTimeout(() => {
                        const gamesSection = document.querySelector('[data-testid="games-grid"]') || 
                                            document.querySelector('.grid') ||
                                            document.querySelector('main');
                        if (gamesSection) {
                          gamesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className="w-full py-2 px-3 text-sm text-center text-primary hover:bg-primary/10 rounded-xl transition-colors"
                  >
                    Tüm sonuçları gör →
                  </button>
                </div>
              </div>,
              document.body
            )}
            
            {/* No Results - Also as Portal */}
            {showResults && query.length >= 2 && searchResults.length === 0 && !isSearching && searchRef.current && ReactDOM.createPortal(
              <div 
                className="fixed bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
                style={{
                  top: searchRef.current.getBoundingClientRect().bottom + 8,
                  left: searchRef.current.getBoundingClientRect().left,
                  width: 320,
                  zIndex: 99999,
                }}
              >
                <div className="p-6 text-center">
                  <Gamepad2 className="size-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">"{query}" için sonuç bulunamadı</p>
                </div>
              </div>,
              document.body
            )}
          </div>

          {/* Message Button - Desktop */}
          {messageButtonEnabled && messageButtonContent && (
            <Button
              variant="outline"
              className="h-10 gap-2 rounded-full text-sm border-primary/30 hover:bg-primary/10"
              onClick={() => setMessageModalOpen(true)}
            >
              <MessageCircle className="size-4" />
              <span className="hidden md:inline">{messageButtonLabel}</span>
            </Button>
          )}

          <Button
            variant="cta"
            className="h-10 gap-2 rounded-full text-sm"
            onClick={() => onOpenRequestForm?.()}
          >
            <FileText className="size-4" />
            <span className="hidden md:inline">Talep Formu</span>
            <span className="md:hidden">Talep</span>
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full hover:bg-accent/50"
            onClick={toggleTheme}
            title={isDarkMode ? "Gündüz Modu" : "Gece Modu"}
            aria-label={isDarkMode ? "Gündüz moduna geç" : "Gece moduna geç"}
          >
            {isDarkMode ? (
              <Sun className="size-5 text-yellow-500" />
            ) : (
              <Moon className="size-5 text-blue-500" />
            )}
          </Button>
        </div>

        {/* Mobile Actions */}
        <div className="flex items-center gap-2 sm:hidden">
          {/* Mobile Search Button - Opens search in mobile menu */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Arama"
            data-testid="mobile-search-button"
          >
            <Search className="size-4 text-muted-foreground" />
          </Button>

          {/* Theme Toggle Mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={toggleTheme}
            aria-label={isDarkMode ? "Gündüz moduna geç" : "Gece moduna geç"}
          >
            {isDarkMode ? (
              <Sun className="size-4 text-yellow-500" />
            ) : (
              <Moon className="size-4 text-blue-500" />
            )}
          </Button>

          <Button
            variant="cta"
            size="sm"
            className="h-9 gap-1.5 rounded-full text-xs px-3"
            onClick={() => onOpenRequestForm?.()}
          >
            <FileText className="size-3.5" />
            Talep
          </Button>

          {/* Mobile Menu Button - Only show on mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="relative border-t border-border/60 p-3 md:hidden animate-fade-up">
          {/* Mobile Search Bar */}
          <div className="relative mb-3" data-testid="mobile-search-bar">
            <form onSubmit={handleSearch}>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                value={query}
                onChange={handleQueryChange}
                onKeyDown={handleKeyDown}
                placeholder="Oyun ara..."
                className={cn(
                  "h-11 rounded-xl border-border/60 bg-background/50 pl-10 pr-10",
                  "shadow-none text-base"
                )}
                data-testid="mobile-search-input"
              />
              {isSearching ? (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground animate-spin" />
              ) : (
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-xl hover:bg-primary/20"
                  aria-label="Ara"
                >
                  <Search className="size-4 text-primary" />
                </Button>
              )}
            </form>
          </div>
          
          <nav className="grid gap-1">
            {resolvedTopNav.map((n, idx) => {
              const Icon = n.icon;
              const href = n.href;
              const key = `mobile-${href}-${idx}`;
              return isExternal(href) ? (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium",
                    "transition-colors hover:bg-accent/50"
                  )}
                  style={{ color: n.color || "#ffffff" }}
                >
                  <Icon className="size-5" />
                  {n.label}
                </a>
              ) : (
                <Link
                  key={key}
                  to={href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium",
                    "transition-colors",
                    isActive(href) ? "bg-accent" : "hover:bg-accent/50"
                  )}
                  style={{ color: n.color || "#ffffff" }}
                >
                  <Icon className="size-5" />
                  {n.label}
                </Link>
              );
            })}
            
            <div className="h-px bg-border/60 my-2" />
            
            {/* Message Button - Mobile */}
            {messageButtonEnabled && messageButtonContent && (
              <button
                onClick={() => {
                  setMessageModalOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
              >
                <MessageCircle className="size-5" />
                {messageButtonLabel}
              </button>
            )}
            
            <button
              onClick={() => {
                onOpenRequestForm?.();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
            >
              <FileText className="size-5" />
              Talep Formu
            </button>
          </nav>
        </div>
      )}
      
      {/* Message Modal - Rendered as Portal to ensure it's on top */}
      {messageModalOpen && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 p-4"
          style={{ zIndex: 999999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setMessageModalOpen(false);
            }
          }}
        >
          <div className="w-full max-w-md rounded-2xl border border-border/60 bg-popover p-5 sm:p-6 shadow-2xl animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
                  <MessageCircle className="size-5" />
                </div>
                <h3 className="text-lg font-semibold">{messageButtonTitle}</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-full"
                onClick={() => setMessageModalOpen(false)}
                aria-label="Kapat"
              >
                <X className="size-4" />
              </Button>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {messageButtonContent}
              </p>
            </div>
            
            <div className="mt-5">
              <Button
                variant="hero"
                className="w-full"
                onClick={() => setMessageModalOpen(false)}
              >
                Tamam
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
